import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortvilleRooms } from '../../../hooks/admin/usePortvilleRooms';
import usePortvilleRoomAuth from '../../../hooks/admin/usePortvilleRoomAuth';
import { useAuth } from '../../../hooks/useAuth';
import RoomForm from '../../../components/admin/portville-rooms/RoomForm';
import { PortvilleRoomFormData } from '../../../types/portvilleRoom';
import { setAdminScrollTop } from '../../../utils/adminScroll';
import { useBackToPortvilleRoomsListOnPop } from '../../../hooks/useBackToPortvilleRoomsListOnPop';
import { markPortvilleRoomsRestoreOnNext } from '../../../utils/portvilleRoomsScroll';

const RoomAddPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();
  const { addRoom } = usePortvilleRooms();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useBackToPortvilleRoomsListOnPop();

  useEffect(() => {
    setAdminScrollTop(0);
  }, []);

  useEffect(() => {
    if (!authLoading && (!user || !hasAccess)) {
      navigate('/admin/dashboard');
      return;
    }
  }, [authLoading, user, hasAccess, navigate]);

  const handleSubmit = async (formData: PortvilleRoomFormData): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      const success = await addRoom(formData);
      if (success) {
        alert('새 호실이 성공적으로 추가되었습니다.');
        markPortvilleRoomsRestoreOnNext();
        navigate('/admin/portville-rooms', { state: { restoreScroll: true } });
        return true;
      }
      return false;
    } catch (error) {
      console.error('호실 추가 실패:', error);
      alert('호실 추가 중 오류가 발생했습니다.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    markPortvilleRoomsRestoreOnNext();
    navigate('/admin/portville-rooms', { state: { restoreScroll: true } });
  };

  if (authLoading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      <div className="max-w-[1600px] mx-auto relative pt-8 px-6">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              새 호실 추가
            </div>
            <button
              onClick={handleCancel}
              className="w-[130px] h-11 bg-[#6b7684] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#6b7684]/90 transition-colors"
            >
              목록으로
            </button>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 폼 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              새 호실 정보
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>

            <RoomForm
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default RoomAddPage;
